/**
 * AddEditAdhocLoadDialog — create OR edit a silica load for an Adhoc client.
 *
 * Isolated flow (does NOT touch AddEditLoadDialog):
 *   • Adhoc client selection via AdhocClientMasterDialog (create mode only)
 *   • On create → POST /api/adhoc-loads → server generates + returns invoice PDF
 *   • On edit   → PUT /api/adhoc-loads/{id} with the changed fields
 *
 * Section layout MIRRORS Balaji/SVE:
 *   1. Basic Info
 *   2. Invoice Details          (read-only in edit)
 *   3. Delivery & Status
 *   4. Updated Invoice           (Balaji-style only)
 *   5. Weight Adjustment         (SVE-style only)
 *   6. Billing
 *   7. Payment
 *
 * Progressive unlock rules match Balaji/SVE:
 *   Basic → Invoice → Delivery → (Balaji Updated | SVE Adjust) → Billing → Payment
 * Locking rules match Balaji/SVE:
 *   • Once bill_status = 'paid', everything is locked
 *   • Sections lock progressively as downstream sections are saved
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Card } from '../../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  Users, FileText, Lock, RefreshCw, TrendingUp, Loader2,
  Info, Truck, DollarSign, CalendarClock, Package, Eye, Download, Upload,
} from 'lucide-react';
import { AdhocClientMasterDialog } from './AdhocClientMasterDialog';
import { CollapsibleSectionHeader } from '../CollapsibleSectionHeader';
import { getTodayString } from '../../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GST_RATE = 0.05;

const b64ToBlob = (b64, type) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
};

const openPdf = (b64) => {
  const blob = b64ToBlob(b64, 'application/pdf');
  window.open(URL.createObjectURL(blob), '_blank');
};

const emptyForm = {
  date: getTodayString(),
  kgs: '',
  rate: '',
  transport_rate: '',
  po_number: '',
  vehicle_number: '',
  product: 'Silica Sand',
  transport_mode: 'By Lorry',
  include_stamp: false,
  // Basic
  material_from: '',
  driver_name: '',
  driver_contact: '',
  transport_company: '',
  // Dual-invoice (Balaji only) — invoice_count 1 or 2, splits kgs
  invoice_count: 1,
  kgs_1: '',
  kgs_2: '',
  // Delivery & Status
  delivery_status: 'in_transit',
  acceptance_status: 'accepted',
  afs_specification: '',
  date_received: '',
  rejection_reason: '',
  // Balaji-style update
  total_kgs_updated: '',
  // SVE-style adjustment
  weight_status: '',              // 'addition' | 'deduction' | ''
  adjustment_kgs: '',
  adjustment_amount: '',
  // Billing (Balaji / SVE parity)
  bill_status: 'pending',
  bill_payment_date: '',
  follow_up_email: '',
  follow_up_phone: '',
  // Payment — all fields below are READ-ONLY & auto-computed. Kept in state
  // only so the backend auto-compute response can hydrate the display.
  initial_due_days: 45,
  expected_payment_date: '',
  actual_payment_date: '',
  past_due: 'no',
};

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

const SECTION_ORDER = [
  { key: 'basic',      label: 'Basic Info',                icon: Info,          bg: 'bg-gradient-to-r from-slate-50 to-slate-100',    accent: 'bg-slate-500',   text: 'text-slate-800',   border: 'border-slate-300'  },
  { key: 'invoice',    label: 'Invoice Details',           icon: FileText,      bg: 'bg-gradient-to-r from-blue-50 to-sky-50',        accent: 'bg-blue-500',    text: 'text-blue-900',    border: 'border-blue-300'   },
  { key: 'status',     label: 'Delivery & Status',         icon: Truck,         bg: 'bg-gradient-to-r from-emerald-50 to-green-50',   accent: 'bg-emerald-500', text: 'text-emerald-900', border: 'border-emerald-300'},
  { key: 'updated',    label: 'Updated Invoice (Balaji)',  icon: RefreshCw,     bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',    accent: 'bg-amber-500',   text: 'text-amber-900',   border: 'border-amber-300'  },
  { key: 'adjust',     label: 'Weight Adjustment (SVE)',   icon: TrendingUp,    bg: 'bg-gradient-to-r from-indigo-50 to-violet-50',   accent: 'bg-indigo-500',  text: 'text-indigo-900',  border: 'border-indigo-300' },
  { key: 'billing',    label: 'Billing',                   icon: DollarSign,    bg: 'bg-gradient-to-r from-orange-50 to-red-50',      accent: 'bg-orange-500',  text: 'text-orange-900',  border: 'border-orange-300' },
  { key: 'payment',    label: 'Payment',                   icon: CalendarClock, bg: 'bg-gradient-to-r from-purple-50 to-fuchsia-50',  accent: 'bg-purple-500',  text: 'text-purple-900',  border: 'border-purple-300' },
];
const secMeta = (k) => SECTION_ORDER.find((s) => s.key === k);

/**
 * FileSlot — reusable file-upload/view widget for adhoc loads.
 * Handles: POST /adhoc-loads/{id}/upload-file  (multipart)
 *          DELETE /adhoc-loads/{id}/upload-file?file_type=…
 * View/Download route through the shared silica-loads download endpoint.
 * Extracted OUTSIDE parent so re-renders don't blow away the <input> ref.
 */
const FileSlot = ({
  label,
  fileType,
  loadId,          // required — file uploads only work post-create
  filename,        // current filename attached to this slot (or null)
  onChanged,       // callback with the updated load doc
  authHeaders,
  onView,
  onDownload,
  disabled = false,
  testid,
}) => {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same filename later
    if (!f) return;
    if (!loadId) {
      toast.error('Save the load first, then attach files.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file_type', fileType);
      fd.append('file', f);
      const r = await axios.post(
        `${API}/adhoc-loads/${loadId}/upload-file`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`${label} uploaded`);
      onChanged && onChanged(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to upload ${label}`);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!loadId) return;
    setBusy(true);
    try {
      const r = await axios.delete(
        `${API}/adhoc-loads/${loadId}/upload-file`,
        { headers: authHeaders(), params: { file_type: fileType } },
      );
      toast.success(`${label} removed`);
      onChanged && onChanged(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to remove ${label}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
         data-testid={testid}>
      <div className="text-xs font-medium text-slate-700">{label}</div>
      {filename ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-600 truncate max-w-[200px]" title={filename}>
            📎 {filename}
          </span>
          <Button size="sm" variant="ghost" className="h-7 px-2"
                  onClick={() => onView && onView(fileType)}
                  data-testid={`${testid}-view`}>
            <Eye className="w-3 h-3 mr-1" />View
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2"
                  onClick={() => onDownload && onDownload(fileType)}
                  data-testid={`${testid}-download`}>
            <Download className="w-3 h-3 mr-1" />Download
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600"
                  onClick={doDelete} disabled={busy || disabled}
                  data-testid={`${testid}-remove`}>
            Remove
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" onChange={doUpload}
                 accept="application/pdf,image/*" className="hidden"
                 data-testid={`${testid}-input`} />
          <Button size="sm" variant="outline"
                  onClick={() => inputRef.current?.click()}
                  disabled={busy || disabled || !loadId}
                  data-testid={`${testid}-btn`}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  : <Upload className="w-3 h-3 mr-1" />}
            {busy ? 'Uploading…' : `Attach ${label}`}
          </Button>
          {!loadId && (
            <span className="text-[10px] text-slate-500">
              (available after creating the load)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Section — extracted OUTSIDE the parent component so React keeps a stable
 * component identity across parent re-renders (each keystroke on any input
 * causes a parent re-render; if Section were defined inside the parent it
 * would be a new component per render → children would remount → inputs
 * would lose focus after every single character).
 */
const Section = ({ name, visible, enabled, locked, hardLocked, expanded, onToggle, children, unlockedExtras }) => {
  if (!visible) return null;
  const meta = secMeta(name);
  return (
    <div
      className={!enabled ? 'opacity-50 pointer-events-none' : ''}
      data-testid={`adhoc-sec-${name}`}
    >
      <CollapsibleSectionHeader
        title={
          <span className="flex items-center gap-2">
            {(locked || hardLocked) && <Lock className="w-3 h-3 opacity-60" />}
            {meta.label}
          </span>
        }
        icon={meta.icon}
        isExpanded={expanded}
        onToggle={onToggle}
        bgColor={meta.bg}
        textColor={meta.text}
        borderColor={meta.border}
        accentColor={meta.accent}
      />
      {expanded && (
        <div
          className={`-mt-2 mb-3 rounded-2xl border ${meta.border} bg-white/60 backdrop-blur-sm p-4`}
          data-testid={`adhoc-sec-body-${name}`}
        >
          <div className={(locked || hardLocked) ? 'pointer-events-none opacity-70' : ''}>
            {children}
          </div>
          {/* unlockedExtras stays interactive even when the section is locked —
              used for descriptive fields (driver, material, transport) that
              don't affect the generated invoice. BUT when hardLocked is set
              (delivery info already saved), even these fields freeze. */}
          {unlockedExtras && (
            <div className={
              hardLocked
                ? 'mt-4 pt-3 border-t border-slate-200 pointer-events-none opacity-70'
                : (locked ? 'mt-4 pt-3 border-t border-slate-200' : '')
            }>
              {unlockedExtras}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function AddEditAdhocLoadDialog({
  open,
  editingLoad = null,
  adminOverride = false, // "Master Edit" — bypass all section locks
  onClose,
  onSaved,
  onRefresh, // optional — called when a file upload/delete changes the load;
             // does NOT close the dialog (unlike onSaved).
}) {
  const isEdit = !!editingLoad?.load_id;

  const [showClientPicker, setShowClientPicker] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Tracks whether an invoice was JUST generated in THIS session but the user
  // has not yet clicked "Save Load" to commit. While true:
  //   • Invoice section stays UNLOCKED (user can still swap signed invoice,
  //     attach permit, etc. — matches user's rule "only lock after save").
  //   • Pressing Cancel will DELETE the half-committed load so the invoice
  //     sequence is released (via the existing DELETE endpoint which also
  //     clears assigned_sequences by source_id).
  const [generatedInSession, setGeneratedInSession] = useState(false);
  const [busySection, setBusySection] = useState(null); // 'updated' | 'adjust' | null
  const [expandedSection, setExpandedSection] = useState('basic');
  // Locally tracked load doc — starts as editingLoad but is updated after
  // each file upload/delete so the FileSlot UI reflects new filenames without
  // needing to close+reopen the dialog.
  const [currentLoad, setCurrentLoad] = useState(editingLoad);
  useEffect(() => { setCurrentLoad(editingLoad); }, [editingLoad]);
  const onFileChanged = (updatedDoc) => {
    setCurrentLoad(updatedDoc);
    onRefresh && onRefresh(updatedDoc); // silent table refresh; keeps dialog open
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  // Tracks dialog-open transitions. We only want to force-reset
  // `expandedSection` back to 'basic' when the dialog FIRST opens — not on
  // every mid-flight load_id change (e.g. the create → edit promotion after
  // "Generate Invoice", when the user's current expansion is 'invoice').
  const prevOpenRef = useRef(false);

  // Hydrate form from editingLoad
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    const firstOpen = !prevOpenRef.current;
    prevOpenRef.current = true;
    if (isEdit) {
      const l = editingLoad;
      const pseudoClient = {
        id: l.adhoc_client_id,
        name: l.adhoc_client_details?.name || l.client_name,
        gstin: l.adhoc_client_details?.gstin || '',
        state: l.adhoc_client_details?.state || '',
        address: l.adhoc_client_details?.address || '',
        rate: l.rate,
        invoice_style: l.invoice_style,
      };
      setSelectedClient(pseudoClient);
      setForm({
        date:                  l.date || l.date_sent || getTodayString(),
        kgs:                   String(l.kgs ?? ''),
        rate:                  String(l.rate ?? ''),
        transport_rate:        l.transport_rate != null ? String(l.transport_rate) : '',
        po_number:             l.purchase_order || '',
        vehicle_number:        l.vehicle_number || '',
        product:               l.product || 'Silica Sand',
        transport_mode:        l.transport_mode || 'By Lorry',
        include_stamp:         false,
        material_from:         l.material_from || '',
        driver_name:           l.driver_name || '',
        driver_contact:        l.driver_contact || '',
        transport_company:     l.transport_company || '',
        delivery_status:       l.delivery_status || 'in_transit',
        acceptance_status:     l.acceptance_status || 'accepted',
        afs_specification:     l.afs_specification || '',
        date_received:         l.date_received || '',
        rejection_reason:      l.rejection_reason || '',
        total_kgs_updated:     l.total_kgs_updated ? String(l.total_kgs_updated) : '',
        weight_status:         l.weight_status || '',
        adjustment_kgs:        l.weight_status === 'addition'
                                 ? String(l.additional_kgs || '')
                                 : l.weight_status === 'deduction'
                                   ? String(l.deduction_kgs || '')
                                   : '',
        adjustment_amount:     l.weight_status === 'addition'
                                 ? String(l.additional_invoice_amount || '')
                                 : l.weight_status === 'deduction'
                                   ? String(l.deduction_invoice_amount || '')
                                   : '',
        bill_status:           l.bill_status || 'pending',
        bill_payment_date:     l.bill_payment_date || '',
        follow_up_email:       l.follow_up_email || '',
        follow_up_phone:       l.follow_up_phone || '',
        initial_due_days:      l.initial_due_days ?? 45,
        expected_payment_date: l.expected_payment_date || '',
        actual_payment_date:   l.actual_payment_date || '',
        past_due:              l.past_due || 'no',
        invoice_count:         l.invoice_count || 1,
        kgs_1:                 l.kgs_1 ? String(l.kgs_1) : '',
        kgs_2:                 l.kgs_2 ? String(l.kgs_2) : '',
      });
      if (firstOpen) setExpandedSection('basic');
    } else {
      setSelectedClient(null);
      setForm(emptyForm);
      if (firstOpen) setExpandedSection('basic');
    }
    // Depend only on load_id so parent partial-refreshes (e.g. after
    // regenerate-updated-invoice) don't nuke user-typed form state like
    // unsaved delivery-info edits. The lock-logic still reads editingLoad
    // live so unlock conditions re-evaluate on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editingLoad?.load_id]);

  const invoiceStyle = selectedClient?.invoice_style; // 'balaji' | 'sve' | undefined
  const isBalaji = invoiceStyle === 'balaji';
  const isSve    = invoiceStyle === 'sve';
  // Source-of-truth for lock evaluation: `currentLoad` is a local copy that
  // reflects in-flight server mutations (regenerate-invoice, file upload, etc.)
  // WITHOUT requiring the dialog to close+reopen. Falls back to `editingLoad`
  // on first render before any mutation.
  const liveLoad = currentLoad || editingLoad;
  const isSveAdditionDeduction = isEdit && liveLoad?.sve_load_type === 'Addition/Deduction';

  // ─────────────────────────────────────────────────────────────
  // Derived amount previews
  // ─────────────────────────────────────────────────────────────
  const amounts = useMemo(() => {
    const k = parseFloat(form.kgs) || 0;
    const r = parseFloat(form.rate) || 0;
    const base = +(k * r).toFixed(2);
    const gst = +(base * GST_RATE).toFixed(2);
    return { base, gst, total: +(base + gst).toFixed(2) };
  }, [form.kgs, form.rate]);

  // ─────────────────────────────────────────────────────────────
  // Section rules — mirror Balaji/SVE progressive unlock + lock
  // ─────────────────────────────────────────────────────────────
  // "Delivery info updated" — the user is only allowed to open the
  // "Updated Invoice" section after confirming delivery of the dispatched
  // material (delivered + accepted + date_received). This also fully locks
  // the Basic Info section (trip details) so staff can't edit the dispatch
  // record once the customer has acknowledged the delivery.
  // Basic info is "complete" (eligible for Generate Invoice) once the
  // user has picked a client + filled date/kgs/rate. The main-content
  // operational fields (PO, vehicle, trip details) are optional at this
  // stage — they can be captured later during Save Load.
  const isBasicInfoComplete = () =>
    !!selectedClient &&
    !!form.date &&
    parseFloat(form.kgs) > 0 &&
    parseFloat(form.rate) > 0 &&
    (
      // single invoice OR invoice_count missing → fine
      parseInt(form.invoice_count, 10) !== 2 ||
      // dual mode (Balaji): both kgs_1 and kgs_2 required and must sum to total
      (
        parseFloat(form.kgs_1) > 0 &&
        parseFloat(form.kgs_2) > 0 &&
        Math.abs((parseFloat(form.kgs_1) + parseFloat(form.kgs_2)) - parseFloat(form.kgs)) < 0.001
      )
    );

  const hasDeliveryInfoSaved = () =>
    isEdit &&
    !!liveLoad?.date_received &&
    liveLoad?.delivery_status === 'delivered' &&
    liveLoad?.acceptance_status === 'accepted';

  const hasInvoiceSaved = () =>
    isEdit && !!liveLoad?.generated_invoice_number;

  const hasUpdatedInvoiceSaved = () =>
    isEdit && !!liveLoad?.total_kgs_updated && (
      !!liveLoad?.generated_updated_invoice_number ||
      !!liveLoad?.updated_invoice_number ||
      !!liveLoad?.updated_invoice_r2_key ||
      !!liveLoad?.generated_invoice_number_2  // legacy pre-reuse records
    );

  const hasWeightAdjustmentSaved = () =>
    isEdit && !!liveLoad?.weight_status && (
      liveLoad?.additional_kgs > 0 || liveLoad?.deduction_kgs > 0
    );

  const isBillPaid = () => isEdit && liveLoad?.bill_status === 'paid';

  const isSectionVisible = (name) => {
    if (name === 'updated') return isBalaji && !isSveAdditionDeduction;
    if (name === 'adjust')  return isSve    && !isSveAdditionDeduction;
    if (name === 'status')  return !isSveAdditionDeduction;
    return true;
  };

  const isSectionEnabled = (name) => {
    if (adminOverride) return true; // Master Edit bypass
    if (!isEdit) {
      // Create mode: sections beyond basic/invoice are gated (need saved load first)
      return name === 'basic' || name === 'invoice';
    }
    switch (name) {
      case 'basic':   return true;
      case 'invoice': return true;
      case 'status':  return hasInvoiceSaved();
      case 'updated': return isBalaji && hasDeliveryInfoSaved();
      case 'adjust':  return isSve    && hasDeliveryInfoSaved();
      case 'billing':
        if (isSveAdditionDeduction) return true;
        if (isBalaji) return hasUpdatedInvoiceSaved();
        if (isSve)    return hasWeightAdjustmentSaved();
        return false;
      case 'payment':
        if (isSveAdditionDeduction) return true;
        if (isBalaji) return hasUpdatedInvoiceSaved();
        if (isSve)    return hasWeightAdjustmentSaved();
        return false;
      default: return true;
    }
  };

  const isSectionLocked = (name) => {
    if (adminOverride) return false; // Master Edit bypass
    if (!isEdit) return false;
    if (isBillPaid()) return true;
    switch (name) {
      case 'basic':   return hasInvoiceSaved();
      // Invoice section stays interactive even when the invoice is already
      // generated. The generation-affecting controls (dual picker, stamp,
      // Generate button) are already hidden via `!isEdit` conditions; the
      // remaining content is just view/download + file attachments (Permit,
      // Signed Invoice), which must stay clickable. Locking the wrapper
      // kills pointer events on child FileSlots → the Attach Permit button
      // becomes a no-op. User-facing rule confirmed:
      // "only after save it should get locked" — Save here = Save Load,
      // which closes the dialog, so lock state after save is moot.
      case 'invoice': return false;
      case 'status':
        if (isBalaji) return hasUpdatedInvoiceSaved();
        if (isSve)    return hasWeightAdjustmentSaved();
        return false;
      case 'updated': return hasUpdatedInvoiceSaved();
      case 'adjust':  return hasWeightAdjustmentSaved();
      default: return false;
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────
  const chooseClient = (c) => {
    setSelectedClient(c);
    setForm((f) => ({ ...f, rate: String(c.rate) }));
    setShowClientPicker(false);
  };

  const validate = () => {
    if (!selectedClient) return 'Please select an adhoc client';
    if (!form.date) return 'Date is required';
    if (!parseFloat(form.kgs) || parseFloat(form.kgs) <= 0) return 'KGs must be > 0';
    if (!parseFloat(form.rate) || parseFloat(form.rate) <= 0) return 'Rate must be > 0';
    return null;
  };

  // Called for both Create (POST) and Edit (PUT)
  const submit = async () => {
    if (isEdit) return submitEdit();
    return submitCreate();
  };

  const submitCreate = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const isBalajiClient = selectedClient?.invoice_style === 'balaji';
    const cnt = parseInt(form.invoice_count, 10) || 1;
    if (isBalajiClient && cnt === 2) {
      const k1 = parseFloat(form.kgs_1), k2 = parseFloat(form.kgs_2);
      if (!k1 || !k2) return toast.error('Enter both kgs_1 and kgs_2 for dual mode');
      if (Math.abs((k1 + k2) - parseFloat(form.kgs)) > 0.001) {
        return toast.error(`kgs_1 + kgs_2 (${k1 + k2}) must equal total kgs (${form.kgs})`);
      }
    }
    setSaving(true);
    try {
      const payload = {
        adhoc_client_id: selectedClient.id,
        date: form.date,
        kgs: parseFloat(form.kgs),
        rate: parseFloat(form.rate),
        transport_rate: form.transport_rate !== '' ? parseFloat(form.transport_rate) : null,
        po_number: form.po_number,
        vehicle_number: form.vehicle_number,
        product: form.product,
        transport_mode: form.transport_mode,
        include_stamp: form.include_stamp,
        // Basic-info fields captured up-front
        driver_name: form.driver_name,
        driver_contact: form.driver_contact,
        transport_company: form.transport_company,
        material_from: form.material_from,
        // Dual-invoice (Balaji only)
        invoice_count: isBalajiClient ? cnt : 1,
        ...(isBalajiClient && cnt === 2 && {
          kgs_1: parseFloat(form.kgs_1),
          kgs_2: parseFloat(form.kgs_2),
        }),
      };
      const r = await axios.post(`${API}/adhoc-loads`, payload, { headers: authHeaders() });
      toast.success(cnt === 2
        ? `Dual invoices created — ${r.data.generated_invoice_number_1} + ${r.data.generated_invoice_number_2}`
        : `Adhoc load created — Invoice ${r.data.generated_invoice_number}`);
      // Intentionally do NOT auto-open the PDF — user can click
      // "View Invoice" / "Download Invoice" from the Invoice section once
      // ready. (Per user feedback: "when we click Generate Invoice,
      // invoice is getting opened — not required".)
      // Keep the dialog OPEN. Promote to edit mode via the fresh server copy
      // so Permit uploads, status edits, etc. unlock immediately. "Save Load"
      // now becomes the footer action that commits any further edits.
      setCurrentLoad(r.data);
      setGeneratedInSession(true);
      onRefresh && onRefresh(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create adhoc load');
    } finally { setSaving(false); }
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      // Patch only the currently-mutable fields. Invoice details are frozen post-generation.
      const patch = {
        date: form.date,
        // transport_rate stays mutable post-generation — it's a reporting
        // helper, not part of the invoice body.
        transport_rate: form.transport_rate !== '' ? parseFloat(form.transport_rate) : null,
        po_number: form.po_number,
        vehicle_number: form.vehicle_number,
        product: form.product,
        transport_mode: form.transport_mode,
        material_from: form.material_from,
        driver_name: form.driver_name,
        driver_contact: form.driver_contact,
        transport_company: form.transport_company,
        delivery_status: form.delivery_status,
        acceptance_status: form.acceptance_status,
        afs_specification: form.afs_specification,
        date_received: form.date_received || null,
        rejection_reason: form.rejection_reason,
        // Billing (Balaji / SVE parity)
        bill_status: form.bill_status,
        bill_payment_date: form.bill_payment_date || null,
        follow_up_email: form.follow_up_email,
        follow_up_phone: form.follow_up_phone,
        // Payment — initial_due_days drives Expected Payment Date auto-compute
        initial_due_days: parseInt(form.initial_due_days, 10) || 45,
      };
      const r = await axios.put(`${API}/adhoc-loads/${editingLoad.load_id}`, patch, {
        headers: authHeaders(),
      });
      // If backend auto-computed expected_payment_date, reflect it locally so
      // the field isn't stale if the user reopens the section.
      if (r.data?.expected_payment_date && r.data.expected_payment_date !== form.expected_payment_date) {
        setF('expected_payment_date', r.data.expected_payment_date);
      }
      toast.success('Adhoc load updated');
      // Clear the "pending generation" flag so a subsequent Cancel does NOT
      // delete the load — the user has explicitly committed via Save Load.
      setGeneratedInSession(false);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update adhoc load');
    } finally { setSaving(false); }
  };

  const runUpdatedInvoice = async () => {
    const k = parseFloat(form.total_kgs_updated);
    if (!k || k <= 0) return toast.error('Enter valid updated kgs');
    setBusySection('updated');
    try {
      const r = await axios.post(
        `${API}/adhoc-loads/${editingLoad.load_id}/generate-updated-invoice`,
        { total_kgs_updated: k, include_stamp: form.include_stamp },
        { headers: authHeaders() },
      );
      const num = r.data.generated_updated_invoice_number ||
                  r.data.updated_invoice_number ||
                  r.data.generated_invoice_number;
      toast.success(`Updated invoice ${num} generated`);
      // Do NOT auto-open the PDF — matches Generate Invoice behaviour above.
      // User can click View / Download from the Updated Invoice section.
      // Refresh the parent list AND the editingLoad reference so the
      // Billing/Payment sections unlock — but KEEP the dialog open so the
      // user can still save unsaved delivery-info edits.
      setCurrentLoad(r.data);
      onRefresh && onRefresh(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate invoice');
    } finally { setBusySection(null); }
  };

  const runAdjustment = async () => {
    const k = parseFloat(form.adjustment_kgs);
    if (!k || k <= 0) return toast.error('Enter valid adjustment kgs');
    if (!form.weight_status) return toast.error('Pick addition or deduction');
    setBusySection('adjust');
    try {
      const payload = {
        adjustment_type: form.weight_status,
        adjustment_kgs: k,
      };
      if (form.adjustment_amount) payload.adjustment_amount = parseFloat(form.adjustment_amount);
      await axios.post(
        `${API}/adhoc-loads/${editingLoad.load_id}/adjustment`,
        payload,
        { headers: authHeaders() },
      );
      toast.success(`Adjustment recorded (${form.weight_status} ${k}kg)`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record adjustment');
    } finally { setBusySection(null); }
  };

  const handleClose = async () => {
    // If the user generated an invoice in this session but never clicked
    // "Save Load" to commit, pressing Cancel must release the invoice
    // number — delete the pending adhoc load. The backend's
    // DELETE /api/adhoc-loads/{id} already removes the sequence rows from
    // `assigned_sequences` (routes/adhoc_loads.py:497), so the next
    // Generate Invoice call will reuse the same sequence number.
    if (generatedInSession && currentLoad?.load_id) {
      try {
        await axios.delete(`${API}/adhoc-loads/${currentLoad.load_id}`,
                           { headers: authHeaders() });
        toast.success('Invoice discarded — sequence number released');
        // Tell the parent to refresh so the discarded row disappears
        // from the list immediately.
        if (onSaved) onSaved();
      } catch (e) {
        toast.error(e?.response?.data?.detail
          || 'Failed to release invoice — please contact admin');
      }
    }
    setGeneratedInSession(false);
    setSelectedClient(null); setForm(emptyForm);
    onClose();
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /**
   * View or download an invoice PDF attached to the current adhoc load.
   * Uses the shared /silica-loads/{load_id}/file/{file_type} endpoint —
   * adhoc loads live in the same silica_loads collection and we mirror the
   * `invoice_r2_key` / `updated_invoice_r2_key` fields at create/update time.
   */
  const viewOrDownload = async (fileType, mode) => {
    if (!editingLoad?.load_id) return;
    try {
      const r = await axios.get(
        `${API}/silica-loads/${editingLoad.load_id}/file/${fileType}`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download =
          (fileType === 'updated_invoice'
            ? editingLoad.updated_invoice_filename
            : editingLoad.invoice_filename) || `${fileType}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${mode} invoice`);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Section state helpers (Section itself is defined outside this
  // component to preserve input focus across parent re-renders).
  // ─────────────────────────────────────────────────────────────
  const sectionProps = (name) => ({
    name,
    visible: isSectionVisible(name),
    enabled: isSectionEnabled(name),
    locked:  isSectionLocked(name),
    // Basic Info is fully frozen (incl. trip-detail unlockedExtras) once the
    // delivery is confirmed — matches the user's rule "don't enable basic
    // info section after saving changes".
    hardLocked: name === 'basic' && hasDeliveryInfoSaved(),
    expanded: expandedSection === name,
    onToggle: () => setExpandedSection(expandedSection === name ? null : name),
  });

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto bg-gradient-to-br from-slate-50 to-white"
          data-testid="add-adhoc-load-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-700" />
              {isEdit ? 'Edit Adhoc Silica Load' : 'New Adhoc Silica Load'}
              {selectedClient && (
                <span className="ml-auto text-xs font-medium px-2.5 py-1 rounded-full bg-slate-900 text-white uppercase tracking-wider">
                  {selectedClient.invoice_style} style
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Client picker / chip */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 space-y-2 shadow-lg"
               data-testid="adhoc-load-client-chip">
            <div className="text-[10px] uppercase tracking-widest text-slate-300">Adhoc Client</div>
            {selectedClient ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2"
                   data-testid="adhoc-load-client-selected">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base truncate">{selectedClient.name}</div>
                  <div className="text-xs text-slate-300 truncate">
                    {selectedClient.gstin || '—'} · {selectedClient.state || '—'}
                  </div>
                </div>
                {!isEdit && (
                  <Button
                    variant="outline" size="sm"
                    className="border-white/30 bg-white/10 text-white hover:bg-white/20"
                    onClick={() => setShowClientPicker(true)}
                    data-testid="adhoc-load-change-client"
                  >
                    Change
                  </Button>
                )}
              </div>
            ) : (
              <Button
                onClick={() => setShowClientPicker(true)}
                className="w-full sm:w-auto bg-white text-slate-900 hover:bg-slate-100"
                data-testid="adhoc-load-pick-client"
              >
                <Users className="w-4 h-4 mr-1" /> Pick or Add Adhoc Client
              </Button>
            )}
          </div>

          {/* Sections */}
          {selectedClient && (
            <div className="space-y-2 mt-2">
              {/* 1. Basic Info — invoice-affecting fields (locked post-invoice) +
                    descriptive fields (always editable via unlockedExtras).  */}
              <Section
                {...sectionProps("basic")}
                unlockedExtras={
                  <div className="space-y-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Trip Details (always editable)
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Transport Company</Label>
                        <Input value={form.transport_company}
                               onChange={(e) => setF('transport_company', e.target.value)}
                               data-testid="adhoc-load-transport-company" />
                      </div>
                      <div>
                        <Label className="text-xs">Driver Name</Label>
                        <Input value={form.driver_name}
                               onChange={(e) => setF('driver_name', e.target.value)}
                               data-testid="adhoc-load-driver-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Driver Contact</Label>
                        <Input value={form.driver_contact}
                               onChange={(e) => setF('driver_contact', e.target.value)}
                               data-testid="adhoc-load-driver-contact" />
                      </div>
                      <div>
                        <Label className="text-xs">Material From</Label>
                        <Input value={form.material_from}
                               onChange={(e) => setF('material_from', e.target.value)}
                               data-testid="adhoc-load-material-from" />
                      </div>
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" value={form.date} max={TODAY_ISO()}
                           onChange={(e) => setF('date', e.target.value)}
                           data-testid="adhoc-load-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Purchase Order #</Label>
                    <Input value={form.po_number}
                           onChange={(e) => setF('po_number', e.target.value)}
                           placeholder="PO-2026-001"
                           data-testid="adhoc-load-po" />
                  </div>
                  <div>
                    <Label className="text-xs">Vehicle Number</Label>
                    <Input value={form.vehicle_number}
                           onChange={(e) => setF('vehicle_number', e.target.value.toUpperCase())}
                           placeholder="KA-01-AB-1234"
                           data-testid="adhoc-load-vehicle" />
                  </div>
                  <div>
                    <Label className="text-xs">Transport Mode</Label>
                    <Input value={form.transport_mode}
                           onChange={(e) => setF('transport_mode', e.target.value)}
                           data-testid="adhoc-load-transport" />
                  </div>
                </div>
              </Section>

              {/* 2. Invoice Details */}
              <Section {...sectionProps("invoice")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Product</Label>
                    <Input value={form.product}
                           onChange={(e) => setF('product', e.target.value)}
                           disabled={isEdit}
                           data-testid="adhoc-load-product" />
                  </div>
                  <div>
                    <Label className="text-xs">KGs *</Label>
                    <Input type="number" step="1" value={form.kgs}
                           onChange={(e) => setF('kgs', e.target.value)}
                           disabled={isEdit}
                           placeholder="25000"
                           data-testid="adhoc-load-kgs" />
                  </div>
                  <div>
                    <Label className="text-xs">Rate (₹/kg) *</Label>
                    <Input type="number" step="0.01" value={form.rate}
                           onChange={(e) => setF('rate', e.target.value)}
                           disabled={isEdit}
                           placeholder="4.50"
                           data-testid="adhoc-load-rate" />
                    {!isEdit && selectedClient
                      && parseFloat(form.rate) !== parseFloat(selectedClient.rate) && (
                        <p className="text-[10px] text-amber-600 mt-1">
                          Overriding master rate (₹{selectedClient.rate}/kg)
                        </p>
                      )}
                  </div>
                  <div>
                    <Label className="text-xs">Transport (₹/kg)</Label>
                    <Input type="number" step="0.01" value={form.transport_rate}
                           onChange={(e) => setF('transport_rate', e.target.value)}
                           placeholder="0.75"
                           data-testid="adhoc-load-transport-rate" />
                    {parseFloat(form.transport_rate) > 0
                      && parseFloat(form.rate) > 0
                      && parseFloat(form.transport_rate) <= parseFloat(form.rate) ? (
                        <p className="text-[10px] text-emerald-600 mt-1">
                          Profit per kg: ₹{(parseFloat(form.rate) - parseFloat(form.transport_rate)).toFixed(2)}
                        </p>
                      ) : parseFloat(form.transport_rate) > parseFloat(form.rate) ? (
                        <p className="text-[10px] text-red-600 mt-1">
                          Transport cannot exceed Rate
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400 mt-1">
                          Rate includes transport. Leave blank if not applicable.
                        </p>
                      )}
                  </div>
                  <div>
                    <Label className="text-xs">Invoice Number</Label>
                    <Input
                      value={editingLoad?.generated_invoice_number || '(auto on create)'}
                      disabled
                      data-testid="adhoc-load-invoice-number"
                    />
                  </div>
                </div>

                {amounts.base > 0 && (
                  <Card className="p-3 bg-emerald-50 border-emerald-200 mt-3"
                        data-testid="adhoc-load-amount-preview">
                    <div className="text-xs text-slate-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Base ({form.kgs}kg × ₹{form.rate}/kg):</span>
                        <span className="font-mono">₹{amounts.base.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>GST @ 5%:</span>
                        <span className="font-mono">₹{amounts.gst.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between border-t border-emerald-300 pt-1 font-semibold text-slate-900">
                        <span>Total (Invoice Amount):</span>
                        <span className="font-mono">₹{amounts.total.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </Card>
                )}

                {!isEdit && (
                  <div className="flex items-center gap-2 mt-3">
                    <Checkbox id="include-stamp" checked={form.include_stamp}
                              onCheckedChange={(v) => setF('include_stamp', !!v)}
                              data-testid="adhoc-load-stamp" />
                    <Label htmlFor="include-stamp" className="text-xs cursor-pointer">
                      Include signature + stamp on invoice
                    </Label>
                  </div>
                )}

                {/* Dual-invoice picker — Balaji-style adhoc, create mode only */}
                {!isEdit && isBalaji && (
                  <div className="mt-4 pt-3 border-t border-blue-200 space-y-3"
                       data-testid="adhoc-load-dual-invoice-section">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                          Number of Invoices
                        </div>
                        <p className="text-[11px] text-slate-500">
                          Balaji-style clients can split one load into 1 or 2 invoices.
                        </p>
                      </div>
                      <Select value={String(form.invoice_count)}
                              onValueChange={(v) => setF('invoice_count', parseInt(v, 10))}>
                        <SelectTrigger className="w-32" data-testid="adhoc-load-invoice-count">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 invoice</SelectItem>
                          <SelectItem value="2">2 invoices</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {parseInt(form.invoice_count, 10) === 2 && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Invoice #1 KGs *</Label>
                          <Input type="number" step="1" value={form.kgs_1}
                                 onChange={(e) => setF('kgs_1', e.target.value)}
                                 placeholder="e.g. 10000"
                                 data-testid="adhoc-load-kgs-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Invoice #2 KGs *</Label>
                          <Input type="number" step="1" value={form.kgs_2}
                                 onChange={(e) => setF('kgs_2', e.target.value)}
                                 placeholder="e.g. 15000"
                                 data-testid="adhoc-load-kgs-2" />
                        </div>
                        {form.kgs && form.kgs_1 && form.kgs_2 && (
                          <div className="col-span-2 text-[11px]">
                            {Math.abs((parseFloat(form.kgs_1) + parseFloat(form.kgs_2)) - parseFloat(form.kgs)) < 0.001
                              ? <span className="text-emerald-600">
                                  ✓ {form.kgs_1} + {form.kgs_2} = {form.kgs} kg
                                </span>
                              : <span className="text-red-600">
                                  ✗ Sum ({parseFloat(form.kgs_1) + parseFloat(form.kgs_2)}) ≠ total kgs ({form.kgs})
                                </span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!isEdit && (
                  <div className="mt-3 p-3 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-900"
                       data-testid="adhoc-load-generate-hint">
                    <b>Note:</b> Fill the Basic Info section above, then click <b>"Generate Invoice{parseInt(form.invoice_count, 10) === 2 ? 's' : ''}"</b> below.
                    After generation, use the <b>View</b> / <b>Download</b> buttons here to open the invoice{parseInt(form.invoice_count, 10) === 2 ? 's' : ''}. The Save Load button unlocks once generated.
                  </div>
                )}

                {/* Generate Invoice button(s) — one for single mode, two for dual.
                    Both dual buttons trigger the same atomic backend call that
                    creates the load with BOTH invoices. */}
                {!isEdit && (
                  <div className="mt-3 flex flex-wrap items-center gap-2"
                       data-testid="adhoc-load-generate-buttons">
                    {parseInt(form.invoice_count, 10) === 2 && isBalaji ? (
                      <>
                        <Button size="sm"
                                onClick={submitCreate}
                                disabled={saving || !isBasicInfoComplete()}
                                data-testid="adhoc-load-generate-invoice-1">
                          <FileText className="w-4 h-4 mr-1" />
                          {saving ? 'Generating…' : 'Generate Invoice #1'}
                        </Button>
                        <Button size="sm"
                                onClick={submitCreate}
                                disabled={saving || !isBasicInfoComplete()}
                                data-testid="adhoc-load-generate-invoice-2">
                          <FileText className="w-4 h-4 mr-1" />
                          {saving ? 'Generating…' : 'Generate Invoice #2'}
                        </Button>
                        <p className="text-[11px] text-slate-500 w-full">
                          Both invoice PDFs are produced in a single atomic server call
                          — clicking either button generates both.
                        </p>
                      </>
                    ) : (
                      <Button size="sm"
                              onClick={submitCreate}
                              disabled={saving || !isBasicInfoComplete()}
                              data-testid="adhoc-load-generate-invoice">
                        <FileText className="w-4 h-4 mr-1" />
                        {saving ? 'Generating…' : 'Generate Invoice'}
                      </Button>
                    )}
                    {!isBasicInfoComplete() && (
                      <span className="text-[11px] text-amber-600">
                        Complete Basic Info (client, date, kgs, rate) to enable.
                      </span>
                    )}
                  </div>
                )}

                {isEdit && editingLoad?.generated_invoice_number && (
                  <div className="mt-3 flex flex-wrap items-center gap-2"
                       data-testid="adhoc-load-invoice-actions">
                    <Button size="sm" variant="outline"
                            onClick={() => viewOrDownload('invoice', 'view')}
                            data-testid="adhoc-load-view-invoice">
                      <Eye className="w-3.5 h-3.5 mr-1" /> View Invoice
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => viewOrDownload('invoice', 'download')}
                            data-testid="adhoc-load-download-invoice">
                      <Download className="w-3.5 h-3.5 mr-1" /> Download Invoice
                    </Button>
                    <span className="text-xs text-slate-500 ml-auto">
                      #{editingLoad.generated_invoice_number}
                    </span>
                  </div>
                )}

                {isEdit && (
                  <div className="mt-4 pt-3 border-t border-blue-200 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Signed / Stamped Invoice (replaces auto-generated)
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Upload a signed / stamped PDF here to <b>replace</b> the auto-generated
                      invoice served by "View / Download Invoice" above — same behavior as
                      Balaji / SVE manual invoice uploads.
                    </p>
                    <FileSlot
                      label="Signed Invoice"
                      fileType="invoice"
                      loadId={currentLoad?.load_id}
                      filename={currentLoad?.invoice_is_manual
                                  ? currentLoad?.invoice_filename : null}
                      onChanged={onFileChanged}
                      authHeaders={authHeaders}
                      onView={(t) => viewOrDownload(t, 'view')}
                      onDownload={(t) => viewOrDownload(t, 'download')}
                      testid="adhoc-file-invoice"
                    />
                  </div>
                )}

                {/* Permit — mandatory attachment that lives with the invoice.
                    Available in both create (hint shown until load saved)
                    and edit modes. */}
                <div className="mt-4 pt-3 border-t border-blue-200 space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    Permit <span className="text-red-500">*</span>
                    <span className="text-[10px] text-slate-400 font-normal normal-case tracking-normal">(required)</span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Attach the transport permit PDF or image — required for every
                    adhoc load before marking delivery.
                  </p>
                  <div className={
                    `rounded-lg ${currentLoad?.permit_filename
                      ? 'ring-1 ring-emerald-200'
                      : 'ring-1 ring-red-200 bg-red-50/40'} p-0.5`
                  }>
                    <FileSlot
                      label="Permit"
                      fileType="permit"
                      loadId={currentLoad?.load_id}
                      filename={currentLoad?.permit_filename}
                      onChanged={onFileChanged}
                      authHeaders={authHeaders}
                      onView={(t) => viewOrDownload(t, 'view')}
                      onDownload={(t) => viewOrDownload(t, 'download')}
                      testid="adhoc-file-permit"
                    />
                  </div>
                </div>
              </Section>

              {/* 3. Delivery & Status */}
              <Section {...sectionProps("status")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Delivery Status</Label>
                    <Select value={form.delivery_status}
                            onValueChange={(v) => setF('delivery_status', v)}>
                      <SelectTrigger data-testid="adhoc-load-delivery-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_transit">In Transit</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Acceptance Status</Label>
                    <Select value={form.acceptance_status}
                            onValueChange={(v) => setF('acceptance_status', v)}>
                      <SelectTrigger data-testid="adhoc-load-acceptance-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date Received</Label>
                    <Input type="date" value={form.date_received} max={TODAY_ISO()}
                           onChange={(e) => setF('date_received', e.target.value)}
                           data-testid="adhoc-load-date-received" />
                  </div>
                  <div>
                    <Label className="text-xs">AFS Specification</Label>
                    <Input value={form.afs_specification}
                           onChange={(e) => setF('afs_specification', e.target.value)}
                           data-testid="adhoc-load-afs" />
                  </div>
                  {form.acceptance_status === 'rejected' && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Rejection Reason</Label>
                      <Input value={form.rejection_reason}
                             onChange={(e) => setF('rejection_reason', e.target.value)}
                             data-testid="adhoc-load-rejection-reason" />
                    </div>
                  )}
                </div>
              </Section>

              {/* 4a. Updated Invoice (Balaji) */}
              <Section {...sectionProps("updated")}>
                {hasUpdatedInvoiceSaved() ? (
                  <div className="text-xs text-slate-600 space-y-2"
                       data-testid="adhoc-updated-invoice-summary">
                    <div>
                      Updated invoice: <b>{editingLoad.generated_invoice_number_2}</b>
                    </div>
                    <div>
                      Updated weight: <b>{editingLoad.total_kgs_updated?.toLocaleString('en-IN')} kg</b>
                    </div>
                    <div>
                      Updated amount: <b>₹{editingLoad.updated_invoice_amount?.toLocaleString('en-IN')}</b>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button size="sm" variant="outline"
                              onClick={() => viewOrDownload('updated_invoice', 'view')}
                              data-testid="adhoc-load-view-updated-invoice">
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => viewOrDownload('updated_invoice', 'download')}
                              data-testid="adhoc-load-download-updated-invoice">
                        <Download className="w-3.5 h-3.5 mr-1" /> Download
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">New Total KGs *</Label>
                      <Input type="number" step="1" value={form.total_kgs_updated}
                             onChange={(e) => setF('total_kgs_updated', e.target.value)}
                             placeholder={`e.g. ${(editingLoad?.kgs || 0) + 200}`}
                             data-testid="adhoc-load-updated-kgs" />
                    </div>
                    <Button size="sm" onClick={runUpdatedInvoice}
                            disabled={busySection === 'updated'}
                            data-testid="adhoc-load-regenerate-invoice">
                      {busySection === 'updated'
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        : <RefreshCw className="w-3 h-3 mr-1" />}
                      Regenerate Invoice
                    </Button>
                  </div>
                )}
                {isEdit && hasUpdatedInvoiceSaved() && (
                  <div className="mt-4 pt-3 border-t border-amber-200 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Signed / Stamped Updated Invoice
                    </div>
                    <FileSlot
                      label="Signed Updated Invoice"
                      fileType="updated_invoice"
                      loadId={currentLoad?.load_id}
                      filename={currentLoad?.updated_invoice_is_manual
                                  ? currentLoad?.updated_invoice_filename : null}
                      onChanged={onFileChanged}
                      authHeaders={authHeaders}
                      onView={(t) => viewOrDownload(t, 'view')}
                      onDownload={(t) => viewOrDownload(t, 'download')}
                      testid="adhoc-file-updated-invoice"
                    />
                  </div>
                )}
              </Section>

              {/* 4b. Weight Adjustment (SVE) */}
              <Section {...sectionProps("adjust")}>
                {hasWeightAdjustmentSaved() ? (
                  <div className="text-xs text-slate-600 space-y-1"
                       data-testid="adhoc-adjustment-summary">
                    <div>Adjustment type: <b>{editingLoad.weight_status}</b></div>
                    <div>Adjustment kgs: <b>{
                      (editingLoad.weight_status === 'addition'
                        ? editingLoad.additional_kgs
                        : editingLoad.deduction_kgs)?.toLocaleString('en-IN')
                    } kg</b></div>
                    <div>Adjustment amount: <b>₹{
                      (editingLoad.weight_status === 'addition'
                        ? editingLoad.additional_invoice_amount
                        : editingLoad.deduction_invoice_amount)?.toLocaleString('en-IN')
                    }</b></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Type *</Label>
                      <Select value={form.weight_status}
                              onValueChange={(v) => setF('weight_status', v)}>
                        <SelectTrigger data-testid="adhoc-load-adj-type">
                          <SelectValue placeholder="Pick" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="addition">+ Addition</SelectItem>
                          <SelectItem value="deduction">− Deduction</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">KGs *</Label>
                      <Input type="number" step="1" value={form.adjustment_kgs}
                             onChange={(e) => setF('adjustment_kgs', e.target.value)}
                             placeholder="200"
                             data-testid="adhoc-load-adj-kgs" />
                    </div>
                    <div>
                      <Label className="text-xs">Amount (₹, auto)</Label>
                      <Input type="number" step="0.01" value={form.adjustment_amount}
                             onChange={(e) => setF('adjustment_amount', e.target.value)}
                             placeholder={`= kgs × ₹${editingLoad?.rate || 0}`}
                             data-testid="adhoc-load-adj-amount" />
                    </div>
                    <Button size="sm" onClick={runAdjustment}
                            disabled={busySection === 'adjust'}
                            data-testid="adhoc-load-adj-save">
                      {busySection === 'adjust'
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        : <TrendingUp className="w-3 h-3 mr-1" />}
                      Record
                    </Button>
                  </div>
                )}
                {isEdit && hasWeightAdjustmentSaved() && (
                  <div className="mt-4 pt-3 border-t border-indigo-200 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {editingLoad.weight_status === 'addition'
                        ? 'Addition Invoice'
                        : 'Deduction Invoice'}
                    </div>
                    <FileSlot
                      label={editingLoad.weight_status === 'addition' ? 'Addition Invoice' : 'Deduction Invoice'}
                      fileType={editingLoad.weight_status === 'addition' ? 'addition_invoice' : 'deduction_invoice'}
                      loadId={currentLoad?.load_id}
                      filename={
                        editingLoad.weight_status === 'addition'
                          ? currentLoad?.addition_invoice_filename
                          : currentLoad?.deduction_invoice_filename
                      }
                      onChanged={onFileChanged}
                      authHeaders={authHeaders}
                      onView={(t) => viewOrDownload(t, 'view')}
                      onDownload={(t) => viewOrDownload(t, 'download')}
                      testid="adhoc-file-adjustment-invoice"
                    />
                  </div>
                )}
              </Section>

              {/* 5. Billing (Balaji / SVE parity) */}
              <Section {...sectionProps("billing")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Bill Status</Label>
                    <Select value={form.bill_status}
                            onValueChange={(v) => setF('bill_status', v)}>
                      <SelectTrigger data-testid="adhoc-load-bill-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Payment Date {form.bill_status === 'paid' ? '*' : ''}
                    </Label>
                    <Input type="date" value={form.bill_payment_date}
                           min={form.date_received || undefined}
                           max={TODAY_ISO()}
                           onChange={(e) => setF('bill_payment_date', e.target.value)}
                           data-testid="adhoc-load-bill-payment-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Follow-up Email</Label>
                    <Input value={form.follow_up_email}
                           onChange={(e) => setF('follow_up_email', e.target.value)}
                           placeholder="accounts@client.com"
                           data-testid="adhoc-load-follow-up-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Follow-up Phone</Label>
                    <Input value={form.follow_up_phone}
                           inputMode="numeric" pattern="[0-9]*"
                           onChange={(e) => setF('follow_up_phone', e.target.value)}
                           placeholder="+91…"
                           data-testid="adhoc-load-follow-up-phone" />
                  </div>
                </div>
              </Section>

              {/* 6. Payment — ENTIRELY READ-ONLY, auto-computed (Balaji/SVE parity).
                     Date Received + (initial_due_days + 15) → expected date.    */}
              <Section {...sectionProps("payment")}>
                {(() => {
                  const dueBase = parseInt(form.initial_due_days, 10) || 45;
                  const dueDays = dueBase + 15;
                  const dr = form.date_received;
                  let expectedDate = form.expected_payment_date;
                  if (!expectedDate && dr) {
                    const d = new Date(dr); d.setDate(d.getDate() + dueDays);
                    expectedDate = d.toISOString().slice(0, 10);
                  }
                  const paid = form.bill_status === 'paid';
                  const paymentDate = form.bill_payment_date || form.actual_payment_date || '';
                  let daysRemaining = '-', pastDue = 'No';
                  if (expectedDate && !paid) {
                    const diff = Math.ceil((new Date(expectedDate) - new Date()) / 86400000);
                    daysRemaining = diff >= 0 ? `${diff} days` : `${Math.abs(diff)} days overdue`;
                    if (diff < 0) pastDue = 'Yes';
                  }
                  return (
                    <div className="space-y-3"
                         data-testid="adhoc-load-payment-auto">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Lock className="w-3 h-3" />
                        <span>All fields auto-computed from Delivery &amp; Billing sections.</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Expected Payment Date</Label>
                          <Input readOnly value={expectedDate || 'Auto-calculated'}
                                 className="bg-slate-50"
                                 data-testid="adhoc-load-expected-payment-date" />
                          <p className="text-[10px] text-purple-600 mt-1">
                            Date Received + {dueDays} days
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs">Days Remaining</Label>
                          <Input readOnly value={daysRemaining}
                                 className={paid ? 'bg-slate-50'
                                                 : (pastDue === 'Yes' ? 'bg-red-50 text-red-700 font-semibold'
                                                                      : 'bg-emerald-50 text-emerald-700 font-semibold')} />
                        </div>
                        <div>
                          <Label className="text-xs">Payment Date</Label>
                          <Input readOnly value={paymentDate || 'Not set'}
                                 className={paymentDate ? 'bg-green-100 text-green-700 font-semibold'
                                                        : 'bg-slate-50 text-slate-400'}
                                 data-testid="adhoc-load-actual-payment-date" />
                          <p className="text-[10px] text-slate-500 mt-1">From Billing section</p>
                        </div>
                        <div>
                          <Label className="text-xs">Past Due</Label>
                          <Input readOnly value={paid ? 'No (Paid)' : pastDue}
                                 className={paid ? 'bg-slate-50'
                                                 : (pastDue === 'Yes' ? 'bg-red-50 text-red-700 font-semibold'
                                                                      : 'bg-emerald-50')}
                                 data-testid="adhoc-load-past-due" />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Section>
            </div>
          )}

          <DialogFooter className="gap-2 mt-3">
            <Button variant="outline" onClick={handleClose} data-testid="adhoc-load-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                saving ||
                !selectedClient ||
                !isEdit ||              // "Save Load" is only live after invoice generation
                !hasInvoiceSaved() ||   // guard — invoice must exist on the server
                (isEdit && isBillPaid() && !adminOverride)
              }
              data-testid="adhoc-load-submit"
              title={!isEdit
                ? 'Generate the invoice first — Save Load unlocks after generation'
                : (!hasInvoiceSaved()
                    ? 'Invoice not yet generated — generate it first'
                    : 'Save all load changes')}
            >
              <FileText className="w-4 h-4 mr-1" />
              {saving ? 'Saving…' : 'Save Load'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdhocClientMasterDialog
        open={showClientPicker}
        onClose={() => setShowClientPicker(false)}
        onSelect={chooseClient}
      />
    </>
  );
}
